import "./index.scss";
import { Button } from "@wise-emotions/react-button";
import { Fieldset } from "@wise-emotions/react-fieldset";
import { GeneralMediumLicensePlate } from "@wise-emotions/react-icons";
import { HelperMessage } from "@wise-emotions/react-helper-message";
import { Label } from "@wise-emotions/react-label";
import { REGEXP_NUMBER } from "@wise-emotions/web-utils";
import { RegisterOptions, useForm } from "react-hook-form";
import { SegmentedControl } from "@wise-emotions/segmented-control";
import { TextInput } from "@wise-emotions/react-form-input";
import { validateOwnerBirthday } from "components/InsuranceQuoter/components/BirthdateInput/utils";
import BirthdateInput from "components/InsuranceQuoter/components/BirthdateInput";
import Editorial from "components/Editorial";
import React, { ReactNode, useState } from "react";

type CustomerType = "newCustomer" | "existingCustomer";

type InsuranceQuoterFormData = {
  plate: string;
  day: string;
  month: string;
  year: string;
};

type SwitchOptions = {
  label: string;
  value: CustomerType;
}[];

export type InsuranceQuoterProps = {
  title: string;
  newCustomerDescription: ReactNode;
  existingCustomerDescription: ReactNode;
  newCustomerLabel: string;
  existingCustomerLabel: string;
  plateInputLabel: string;
  plateInputPlaceholder: string;
  plateRequiredError: string;
  platePatternError: string;
  birthdateInputLabel: string;
  dayInputPlaceholder: string;
  monthInputPlaceholder: string;
  yearInputPlaceholder: string;
  birthdateRequiredError: string;
  birthdatePatternError: string;
  ctaLabel: string;
  newCustomerRedirectUrl: string;
  existingCustomerRedirectUrl: string;
  backgroundImageSrc: string;
  dataTrackingForNewCustomer: string;
  dataTrackingForExistingCustomer: string;
  dataPosition: string;
};

const InsuranceQuoter = ({
  title,
  newCustomerLabel,
  existingCustomerLabel,
  newCustomerDescription,
  existingCustomerDescription,
  plateInputLabel,
  plateInputPlaceholder,
  plateRequiredError,
  platePatternError,
  birthdateInputLabel,
  dayInputPlaceholder,
  monthInputPlaceholder,
  yearInputPlaceholder,
  birthdateRequiredError,
  birthdatePatternError,
  ctaLabel,
  newCustomerRedirectUrl,
  existingCustomerRedirectUrl,
  backgroundImageSrc,
  dataTrackingForNewCustomer,
  dataTrackingForExistingCustomer,
  dataPosition,
}: InsuranceQuoterProps) => {
  const options: SwitchOptions = [
    {
      label: newCustomerLabel,
      value: "newCustomer",
    },
    {
      label: existingCustomerLabel,
      value: "existingCustomer",
    },
  ];
  const imageStyle = { backgroundImage: `url(${backgroundImageSrc}?fm=webp)` };
  const [switchValue, setSwitchValue] = useState<CustomerType>(
    options[0].value,
  );
  const { register, handleSubmit, formState, setError } =
    useForm<InsuranceQuoterFormData>();

  const birthdateFormRegisterOptions: RegisterOptions<InsuranceQuoterFormData> =
    {
      required: birthdateRequiredError,
      pattern: {
        value: REGEXP_NUMBER,
        message: birthdatePatternError,
      },
    };

  const URL_CONFIG: Record<CustomerType, string> = {
    newCustomer: newCustomerRedirectUrl,
    existingCustomer: existingCustomerRedirectUrl,
  };

  const onSubmit = ({ plate, day, month, year }: InsuranceQuoterFormData) => {
    const { validate } = validateOwnerBirthday();

    if (!validate(`${year}-${month}-${day}`)) {
      setError("day", { message: birthdatePatternError });
      setError("month", { message: birthdatePatternError });
      setError("year", { message: birthdatePatternError });
      return;
    }

    const queryParams = new URLSearchParams({
      targa: plate.trim().toUpperCase(),
      data: `${day}/${month}/${year}`,
    });
    const baseUrl = URL_CONFIG[switchValue];
    const finalUrl = `${baseUrl}?${queryParams.toString()}`;
    window.open(finalUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="insurance-quoter" style={imageStyle}>
      <div className="insurance-quoter__title">
        <Editorial titleTag="h3" title={title} />
      </div>
      <div className="insurance-quoter__switch">
        <SegmentedControl
          options={options}
          currentValue={switchValue}
          onChangeValue={(value) => setSwitchValue(value as CustomerType)}
          size="sm"
        />
      </div>
      <div className="insurance-quoter__description">
        {switchValue === "newCustomer"
          ? newCustomerDescription
          : existingCustomerDescription}
      </div>
      <form
        className="insurance-quoter__form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="insurance-quoter__inputs">
          <Fieldset
            label={
              <Label
                htmlFor="plate"
                variant="default"
                title={plateInputLabel}
                isBold
              />
            }
            input={
              <TextInput
                {...register("plate", {
                  required: plateRequiredError,
                  pattern: {
                    value: /^[a-zA-Z0-9]{3,10}$/,
                    message: platePatternError,
                  },
                })}
                aria-describedby="plate-message"
                aria-errormessage="plate-validation"
                placeholder={plateInputPlaceholder}
                iconPosition="right"
                valid={!formState.errors.plate}
                Icon={GeneralMediumLicensePlate}
              />
            }
            helperMessage={
              <HelperMessage
                errorMessage={formState.errors.plate?.message}
                idMessage="plate-message"
                idValidation="plate-validation"
              />
            }
          />
          <Fieldset
            label={
              <Label
                htmlFor="birthdate"
                variant="default"
                title={birthdateInputLabel}
                isBold
              />
            }
            input={
              <div className="insurance-quoter__date-fields">
                <BirthdateInput
                  {...register("day", birthdateFormRegisterOptions)}
                  placeholder={dayInputPlaceholder}
                  valid={!formState.errors.day}
                  maxLength={2}
                />
                <BirthdateInput
                  {...register("month", birthdateFormRegisterOptions)}
                  placeholder={monthInputPlaceholder}
                  valid={!formState.errors.month}
                  maxLength={2}
                />
                <BirthdateInput
                  {...register("year", birthdateFormRegisterOptions)}
                  placeholder={yearInputPlaceholder}
                  valid={!formState.errors.year}
                  maxLength={4}
                />
              </div>
            }
            helperMessage={
              <HelperMessage
                errorMessage={
                  formState.errors.day?.message ||
                  formState.errors.month?.message ||
                  formState.errors.year?.message
                }
                idValidation="birthdate-validation"
              />
            }
          />
        </div>
        <div className="insurance-quoter__button">
          <Button
            variant="default"
            type="submit"
            data-tracking={
              switchValue === "newCustomer"
                ? dataTrackingForNewCustomer
                : dataTrackingForExistingCustomer
            }
            data-position={dataPosition}
          >
            {ctaLabel}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default InsuranceQuoter;
